import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ModuleHeader } from '@/components/ui/module-header';
import { LiveStatusStrip } from '@/components/ui/live-status-strip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

export default function SuperOverviewPage() {
  const q = useQuery({
    queryKey: ['super-overview'],
    queryFn: async () => {
      const r = await api('/api/v1/super/overview');
      if (r.status !== 200) throw new Error('overview');
      return r.data;
    },
  });
  const d = q.data;

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Super Admin"
          description="Cross-tenant control plane — company admins cannot access this console."
          icon="LayoutDashboard"
          metrics={[
            { label: 'Companies', value: d?.companies ?? '—' },
            { label: 'Users', value: d?.users ?? '—' },
            { label: 'Open errors 24h', value: d?.openErrors24h ?? '—' },
            { label: 'Requests 24h', value: d?.requestVolume24h ?? '—' },
          ]}
        />

        <LiveStatusStrip
          metrics={[
            {
              label: '5xx (24h)',
              value: String(d?.status5xx24h ?? 0),
              className: (d?.status5xx24h ?? 0) > 0 ? 'text-yellow-500' : 'text-green-500',
            },
            {
              label: 'Past due tenants',
              value: String(d?.billingMix?.past_due ?? 0),
              className: 'text-yellow-500',
            },
            {
              label: 'Active billing',
              value: String(d?.billingMix?.active ?? 0),
              className: 'text-green-500',
            },
          ]}
        />

        {Object.keys(d?.planMix ?? {}).length > 0 && (
          <div className="flex flex-wrap gap-3">
            {Object.entries(d.planMix).map(([plan, n]) => (
              <div
                key={plan}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
              >
                <span className="text-muted-foreground">{plan}</span>
                <span className="font-bold data-text">{n}</span>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick links</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button asChild variant="outline" className="justify-start">
                <Link to="/super/errors">Error tracer</Link>
              </Button>
              <Button asChild variant="outline" className="justify-start">
                <Link to="/super/audit">Audit logs</Link>
              </Button>
              <Button asChild variant="outline" className="justify-start">
                <Link to="/super/metrics">Request metrics</Link>
              </Button>
              <Button asChild variant="outline" className="justify-start">
                <Link to="/super/marketing">Marketing</Link>
              </Button>
              <Button asChild variant="outline" className="justify-start">
                <Link to="/super/apps">Apps catalog</Link>
              </Button>
              <Button asChild variant="outline" className="justify-start">
                <Link to="/super/security">Security status</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing mix</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <tbody className="divide-y">
                  {Object.entries(d?.billingMix ?? {}).map(([k, v]) => (
                    <tr key={k}>
                      <td className="py-2">{k}</td>
                      <td className="py-2 text-right font-mono data-text">{v}</td>
                    </tr>
                  ))}
                  {!d && (
                    <tr>
                      <td colSpan={2} className="py-2 text-muted-foreground">
                        Loading…
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
);
}
